import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";
import * as XLSX from "xlsx";
import { FiRefreshCcw } from "react-icons/fi";

const today = () => new Date().toISOString().split("T")[0];

/* ── Skeleton row ── */
const SkeletonRow = () => (
    <tr className="animate-pulse">
        {[50, 200, 180, 100, 110, 130, 120, 130, 130, 130, 110, 110, 90, 90, 110].map((w, i) => (
            <td key={i} className="border border-[#E5E7EB] px-3 py-3.5" style={{ minWidth: w }}>
                <div className="h-3.5 bg-gray-200 rounded-full" style={{ width: "70%" }} />
            </td>
        ))}
    </tr>
);

/* ── Empty state ── */
const EmptyState = ({ onReset }) => (
    <tr>
        <td colSpan={15} className="border-0 py-24 text-center">
            <div className="flex flex-col items-center gap-4 px-6">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                <div>
                    <p className="text-base font-medium text-gray-700">No records found</p>
                    <p className="text-gray-400 text-sm mt-1">Try a different date or click Refresh.</p>
                </div>
                <button onClick={onReset}
                    className="px-5 py-2 rounded-lg bg-[#0A2A4F] text-white font-medium text-sm hover:bg-[#0d3566] transition shadow-sm">
                    Reset & Reload
                </button>
            </div>
        </td>
    </tr>
);

const OdOutstanding = () => {

    /* ── State ── */
    const [date, setDate] = useState(today());
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [downloadError, setDownloadError] = useState("");

    /* ── Pagination ── */
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [goToPage, setGoToPage] = useState("");

    // reconstruct
    const [reconstructDate, setReconstructDate] = useState(today());
    const [reconstructLoading, setReconstructLoading] = useState(false);
    const [reconstructData, setReconstructData] = useState(null);
    const [showReconstructModal, setShowReconstructModal] = useState(false);
    const [toast, setToast] = useState(null);

    /* ── API ── */
    const { callApi: fetchReport } = useApi("/od/admin/report", "GET");

    const { callApi: reconstructReportApi } = useApi("/od/admin/report/reconstruct", "GET");

    /* ── Smart pagination ── */
    const getPaginationPages = (current, total) => {
        if (total <= 1) return [];
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const pages = [];
        const delta = 2;
        let prev = null;
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current + 1 - delta && i <= current + 1 + delta)) {
                if (prev !== null && i - prev > 1) pages.push("...");
                pages.push(i);
                prev = i;
            }
        }
        return pages;
    };

    const paginationPages = getPaginationPages(page, totalPages);
    const rangeStart = totalElements === 0 ? 0 : page * size + 1;
    const rangeEnd = Math.min(page * size + data.length, totalElements);

    /* ── Fetch ── */
    const fetchData = async (targetPage = 0) => {
        try {
            setLoading(true);
            const query = `?date=${date}&page=${targetPage}&size=${size}`;
            const res = await fetchReport(null, query);

            if (res && res.content) {
                setData(res.content);
                setPage(res.number ?? targetPage);
                setTotalPages(res.totalPages ?? 0);
                setTotalElements(res.totalElements ?? 0);
                setIsLastPage(res.last === true);
            } else if (Array.isArray(res)) {
                setData(res);
                setPage(0);
                setTotalPages(0);
                setTotalElements(res.length);
                setIsLastPage(true);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error(err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setPage(0);
        fetchData(0);
    };

    const handleFetchForPage = (targetPage) => {
        setPage(targetPage);
        fetchData(targetPage);
    };

    const handleGoToPage = () => {
        const p = parseInt(goToPage, 10);
        if (isNaN(p) || p < 1 || p > totalPages) {
            setDownloadError(`Enter a page between 1 and ${totalPages}`);
            return;
        }
        setDownloadError("");
        handleFetchForPage(p - 1);
        setGoToPage("");
    };

    useEffect(() => {
        fetchData(0);
        // eslint-disable-next-line
    }, []);

    /* ── Export current page ── */
    const downloadPageExcel = () => {
        if (!data.length) return;
        const ws = XLSX.utils.json_to_sheet(data.map(toExportRow));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "OD Outstanding");
        XLSX.writeFile(wb, `OD_Outstanding_${date}_page${page + 1}.xlsx`);
    };

    /* ── Export all pages ── */
    const downloadAllExcel = async () => {
        try {
            setExcelLoading(true);
            const exportSize = totalElements > 0 ? totalElements : size;
            const query = `?date=${date}&page=0&size=${exportSize}`;
            const res = await fetchReport(null, query);
            let rows = [];
            if (res && res.content) rows = res.content;
            else if (Array.isArray(res)) rows = res;
            if (!rows.length) return;
            const ws = XLSX.utils.json_to_sheet(rows.map(toExportRow));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "OD Outstanding");
            XLSX.writeFile(wb, `OD_Outstanding_${date}_all.xlsx`);
        } catch (err) {
            console.error(err);
        } finally {
            setExcelLoading(false);
        }
    };

    const toExportRow = (r) => ({
        "Date":                    r.date ?? date,
        "User ID":                 r.userId ?? "-",
        "BP ID":                   r.bpId ?? "-",
        "Full Name":               r.fullName ?? "-",
        "Mobile":                  r.mobile ?? "-",
        "Shop Name":               r.shopName ?? "-",
        "Role":                    r.role ?? "-",
        "Credit Limit":            r.creditLimit ?? 0,
        "Sanctioned Amount":       r.sanctionedAmount ?? 0,
        "Utilized Amount":         r.utilizedAmount ?? 0,
        "Available Limit":         r.availableLimit ?? 0,
        "Penalty Amount":          r.penaltyAmount ?? 0,
        "Penalty Type":            r.penaltyType ?? "-",
        "Pending Penalty Amount":  r.pendingPenaltyAmount ?? 0,
        "Status":                  r.status ?? "-",
        "OD Enabled":              r.isOdEnabled ? "Yes" : "No",
        "EOD Failure Attempts":    r.eodFailureAttempts ?? 0,
    });

    const fmt = (n) =>
        typeof n === "number"
            ? `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : (n != null ? `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-");

    const statusCls = (status) => {
        const s = String(status ?? "").toUpperCase();
        if (s === "ACTIVE") return "bg-green-100 text-green-700 border-green-200";
        if (s === "BLOCKED") return "bg-amber-100 text-amber-700 border-amber-200";
        if (s === "DISABLED") return "bg-red-100 text-red-700 border-red-200";
        return "bg-gray-100 text-gray-600 border-gray-200";
    };

    const showToastMessage = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    const handleReconstructReport = async () => {
        if (!reconstructDate) {
            showToastMessage("error", "Please select a date for reconstruction.");
            return;
        }
        setReconstructLoading(true);
        try {
            const res = await reconstructReportApi(null, `?date=${reconstructDate}`);
            const csvText = typeof res === 'string' ? res : res?.data;

            if (csvText && csvText.includes("date,userId")) {
                const lines = csvText.trim().split('\n').filter(line => line.trim() !== "");
                
                // Parse headers and prepend S.No.
                const originalHeaders = lines[0].split(',');
                const headers = ["S.No.", ...originalHeaders];

                // Parse rows and prepend index
                const parsedData = lines.slice(1).map((line, rowIndex) => {
                    const values = line.split(',');
                    const obj = { "S.No.": rowIndex + 1 }; // Add Numbering
                    originalHeaders.forEach((header, index) => {
                        obj[header.trim()] = values[index];
                    });
                    return obj;
                });

                setReconstructData({ headers, rows: parsedData });
                setShowReconstructModal(true);
                showToastMessage("success", "OD Report fetched successfully.");
            } else {
                showToastMessage("error", res?.message || "Request executed, but no valid CSV data returned.");
            }
        } catch (err) {
            showToastMessage("error", err?.response?.data?.message || err?.message || "Failed to reconstruct report.");
        } finally {
            setReconstructLoading(false);
        }
    };

    const downloadReconstructCSV = () => {
        if (!reconstructData) return;
        const csvContent = [
            reconstructData.headers.join(','),
            ...reconstructData.rows.map(row => reconstructData.headers.map(h => row[h]).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `OD_Reconstructed_Report_${reconstructDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    /* ── Styles ── */
    const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-2 py-2 text-left text-[10px] font-semibold text-[#0A2A4F] uppercase tracking-wide whitespace-nowrap select-none";
    const TD = "border border-[#E5E7EB] px-3 py-2.5 text-[13px] text-gray-700 align-middle";

    /* ════════════════════════ RENDER ════════════════════════ */
    return (
        <div className="h-screen flex flex-col overflow-hidden bg-[#F0F4FA]" style={{ fontFamily: "'Inter','ui-sans-serif',system-ui,sans-serif" }}>

            {/* ══ HEADER ══ */}
            <div className="flex-shrink-0 z-20 shadow-sm">

                {/* Title bar */}
                <div className="bg-white border-b border-[#E5E7EB] px-5 py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-[#0A2A4F] flex items-center justify-center text-white text-lg font-medium flex-shrink-0">OD</div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2.5 flex-wrap">
                                <h1 className="text-[20px] font-normal text-[#0A2A4F] leading-tight">
                                    OD Outstanding Report
                                </h1>
                                {totalElements > 0 && (
                                    <span className="px-3 py-1 bg-[#EFF6FF] border border-[#BFDBFE] text-[#0A2A4F] rounded-full text-[13px] font-medium whitespace-nowrap">
                                        {totalElements.toLocaleString("en-IN")} records
                                    </span>
                                )}
                            </div>
                            {totalPages > 0 && (
                                <p className="text-[12px] text-gray-400 font-normal leading-tight mt-0.5">
                                    Page {page + 1} of {totalPages} · {date}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {loading && (
                            <div className="w-4 h-4 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mr-1" />
                        )}
                        <button onClick={downloadPageExcel} disabled={!data.length}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#EFF6FF] disabled:opacity-40 text-[#0A2A4F] font-medium text-[13px] transition">
                            ↓ Page XLS
                        </button>
                        <button onClick={downloadAllExcel} disabled={excelLoading || !totalElements}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#16A34A] hover:bg-[#15803d] disabled:opacity-50 text-white font-medium text-[13px] transition">
                            ↓ {excelLoading ? "Exporting…" : `Export All${totalElements > 0 ? ` (${totalElements.toLocaleString("en-IN")})` : ""}`}
                        </button>
                        <button onClick={handleRefresh} disabled={loading}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[13px] transition">
                            <FiRefreshCcw size={13} className={loading ? "animate-spin" : ""} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Summary strip */}
                <div className="bg-white border-b border-[#E5E7EB] px-4 flex items-stretch divide-x divide-[#E5E7EB] overflow-x-auto">
                    <div className="flex items-center gap-2.5 px-6 py-3 bg-[#EFF6FF] select-none flex-shrink-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0 bg-[#0A2A4F]" />
                        <span className="text-[12px] font-medium text-gray-400">Total Records</span>
                        <span className="text-[17px] font-normal tabular-nums text-[#0A2A4F]">{totalElements.toLocaleString("en-IN")}</span>
                    </div>
                </div>

                {/* Filter bar */}
                {/* <div className="bg-white border-b border-[#E5E7EB] px-3 py-3 flex gap-2 items-center overflow-x-auto">
                    <span className="text-[13px] text-gray-500 font-medium whitespace-nowrap flex-shrink-0">Date</span>
                    <input
                        type="date"
                        value={date}
                        max={today()}
                        onChange={(e) => setDate(e.target.value)}
                        className="h-[42px] px-3 border border-[#E5E7EB] rounded-lg bg-white text-[14px] text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none flex-shrink-0"
                    />
                    <button onClick={handleRefresh} disabled={loading}
                        className="h-[42px] px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[14px] transition flex-shrink-0 flex items-center gap-2">
                        <FiRefreshCcw size={14} className={loading ? "animate-spin" : ""} />
                        Apply
                    </button>
                </div> */}

                {/* Filter & Admin Actions Bar */}
    <div className="bg-white border-b border-[#E5E7EB] px-3 py-3 flex gap-4 items-center overflow-x-auto justify-between">
        
        {/* View Standard Report */}
        <div className="flex gap-2 items-center flex-shrink-0">
            <span className="text-[13px] text-gray-500 font-medium whitespace-nowrap">Report Date</span>
            <input
                type="date"
                value={date}
                max={today()}
                onChange={(e) => setDate(e.target.value)}
                className="h-[42px] px-3 border border-[#E5E7EB] rounded-lg bg-white text-[14px] text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none"
            />
            <button onClick={handleRefresh} disabled={loading}
                className="h-[42px] px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[14px] transition flex items-center gap-2">
                <FiRefreshCcw size={14} className={loading ? "animate-spin" : ""} />
                Apply
            </button>
        </div>

        {/* ✅ ADDED: RECONSTRUCT REPORT SECTION */}
        <div className="flex gap-2 items-center flex-shrink-0 border-l border-[#E5E7EB] pl-4">
            <span className="text-[13px] text-amber-600 font-bold whitespace-nowrap">EOD Admin Reconstruct</span>
            <input
                type="date"
                value={reconstructDate}
                max={today()}
                onChange={(e) => setReconstructDate(e.target.value)}
                className="h-[42px] px-3 border border-amber-200 rounded-lg bg-amber-50 text-[14px] text-amber-900 focus:ring-2 focus:ring-amber-500/30 outline-none"
            />
            <button onClick={handleReconstructReport} disabled={reconstructLoading}
                className="h-[42px] px-5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium text-[14px] transition flex items-center gap-2 shadow-sm">
                {reconstructLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                )}
                Execute
            </button>
        </div>
    </div>

            </div>

            {/* ══ TABLE ══ */}
            <div className="flex-1 overflow-hidden flex flex-col px-3 pt-3 pb-16">
                <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm flex flex-col flex-1 overflow-hidden">

                    {/* Mobile cards */}
                    <div className="block md:hidden flex-1 overflow-y-auto divide-y divide-gray-100">
                        {loading ? (
                            <div className="py-12 flex justify-center">
                                <div className="w-8 h-8 border-4 border-[#0A2A4F] border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : data.length > 0 ? (
                            data.map((row, i) => (
                                <div key={i} className="px-4 py-4 space-y-2">
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <p className="text-[13px] font-semibold text-[#0A2A4F] truncate">{row.fullName ?? "-"}</p>
                                            <p className="text-[11px] text-gray-500 font-mono font-bold mt-0.5">{row.bpId ?? "-"}</p>
                                            <p className="text-[12px] text-gray-600 mt-0.5">{row.mobile ?? "-"}</p>
                                            {row.shopName && <p className="text-[11px] text-gray-700 font-bold">{row.shopName}</p>}
                                        </div>
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            {row.status && (
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${statusCls(row.status)}`}>
                                                    {row.status}
                                                </span>
                                            )}
                                            <span className="text-[10px] text-gray-400">{row.role ?? ""}</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                                        <div className="flex justify-between"><span className="text-gray-400">Sanctioned</span><span className="font-medium text-gray-700">{fmt(row.sanctionedAmount)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Credit Limit</span><span className="font-medium text-gray-700">{fmt(row.creditLimit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Utilized</span><span className="font-medium text-red-600">{fmt(row.utilizedAmount)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Available</span><span className="font-medium text-green-600">{fmt(row.availableLimit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Pending Penalty</span><span className="font-medium text-amber-600">{fmt(row.pendingPenaltyAmount)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Penalty</span><span className="font-medium text-gray-700">{row.penaltyAmount ?? "-"} {row.penaltyType ?? ""}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">EOD Fails</span><span className="font-medium text-gray-700">{row.eodFailureAttempts ?? 0}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">OD Enabled</span><span className="font-medium text-gray-700">{row.isOdEnabled ? "Yes" : "No"}</span></div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-16 flex flex-col items-center gap-3 text-center px-4">
                                <div className="text-4xl">📭</div>
                                <p className="text-sm font-medium text-gray-600">No records found</p>
                                <button onClick={handleRefresh} className="px-4 py-2 rounded-lg bg-[#0A2A4F] text-white text-sm">Refresh</button>
                            </div>
                        )}

                        {/* Mobile pagination */}
                        {!loading && totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 py-4 flex-wrap px-4">
                                <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                                    className="px-3 py-1.5 rounded-lg border text-[13px] bg-white text-[#0A2A4F] disabled:opacity-30">‹ Prev</button>
                                <span className="text-[13px] text-gray-500">Page {page + 1} of {totalPages}</span>
                                <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                                    className="px-3 py-1.5 rounded-lg border text-[13px] bg-white text-[#0A2A4F] disabled:opacity-30">Next ›</button>
                            </div>
                        )}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block flex-1 overflow-auto">
                        <table className="w-full border-collapse text-[13px]" style={{ minWidth: 1700 }}>
                            <thead className="sticky top-0 z-10">
                                <tr>
                                    <th className={TH} style={{ width: 50 }}>#</th>
                                    <th className={TH}>User Info</th>
                                    <th className={TH}>Contact & Shop</th>
                                    <th className={TH}>Role</th>
                                    <th className={TH} style={{ width: 110 }}>Date</th>
                                    <th className={`${TH} text-right`}>Sanctioned Amt</th>
                                    <th className={`${TH} text-right`}>Credit Limit</th>
                                    <th className={`${TH} text-right`}>Utilized Amt</th>
                                    <th className={`${TH} text-right`}>Available Limit</th>
                                    <th className={`${TH} text-right`}>Pending Penalty</th>
                                    <th className={`${TH} text-right`}>Penalty Amt</th>
                                    <th className={TH}>Penalty Type</th>
                                    <th className={`${TH} text-center`}>EOD Fails</th>
                                    <th className={`${TH} text-center`}>OD Enabled</th>
                                    <th className={TH}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                                ) : data.length > 0 ? (
                                    data.map((row, i) => (
                                        <tr key={i} className="hover:bg-[#F8FAFF] transition-colors">
                                            <td className={`${TD} text-gray-400 text-center`}>{rangeStart + i}</td>

                                            {/* User Info: Full Name + BP ID */}
                                            <td className={TD}>
                                                <div className="font-semibold text-[#0A2A4F] leading-snug">{row.fullName ?? "-"}</div>
                                                <div className="text-[11px] text-gray-500 mt-0.5 font-mono font-bold">
                                                    {row.bpId ?? "-"}
                                                </div>
                                            </td>

                                            {/* Contact & Shop: Mobile + Shop Name */}
                                            <td className={TD}>
                                                <div className="font-medium text-gray-800">{row.mobile ?? "-"}</div>
                                                <div className="text-[11px] text-gray-700 mt-0.5 font-bold">{row.shopName ?? "-"}</div>
                                            </td>

                                            <td className={TD}>
                                                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-[#EFF6FF] text-[#0A2A4F] border border-[#BFDBFE]">
                                                    {row.role ?? "-"}
                                                </span>
                                            </td>
                                            <td className={TD}>{row.date ?? date}</td>
                                            <td className={`${TD} text-right font-medium text-gray-700`}>{fmt(row.sanctionedAmount)}</td>
                                            <td className={`${TD} text-right font-medium text-gray-700`}>{fmt(row.creditLimit)}</td>
                                            <td className={`${TD} text-right font-medium text-red-600`}>{fmt(row.utilizedAmount)}</td>
                                            <td className={`${TD} text-right font-medium text-green-600`}>{fmt(row.availableLimit)}</td>
                                            <td className={`${TD} text-right text-amber-600`}>{fmt(row.pendingPenaltyAmount)}</td>
                                            <td className={`${TD} text-right text-gray-700`}>
                                                {row.penaltyAmount != null
                                                    ? (row.penaltyType === "PERCENTAGE"
                                                        ? `${row.penaltyAmount}%`
                                                        : fmt(row.penaltyAmount))
                                                    : "-"}
                                            </td>
                                            <td className={TD}>{row.penaltyType ?? "-"}</td>
                                            <td className={`${TD} text-center`}>
                                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold ${
                                                    (row.eodFailureAttempts ?? 0) > 0
                                                        ? "bg-red-50 text-red-700 border border-red-200"
                                                        : "bg-gray-100 text-gray-500"
                                                }`}>
                                                    {row.eodFailureAttempts ?? 0}
                                                </span>
                                            </td>
                                            <td className={`${TD} text-center`}>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${
                                                    row.isOdEnabled ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"
                                                }`}>
                                                    {row.isOdEnabled ? "Yes" : "No"}
                                                </span>
                                            </td>
                                            <td className={TD}>
                                                {row.status ? (
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold border ${statusCls(row.status)}`}>
                                                        {row.status}
                                                    </span>
                                                ) : "-"}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <EmptyState onReset={handleRefresh} />
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    {!loading && totalPages > 0 && (
                        <div className="flex-shrink-0 border-t border-[#E5E7EB] bg-white px-4 py-2.5 flex items-center justify-between gap-3 overflow-x-auto flex-nowrap">

                            {/* LEFT: range + Prev / pages / Next */}
                            <div className="flex items-center gap-1.5 flex-nowrap flex-shrink-0">
                                {totalElements > 0 && (
                                    <span className="text-[12px] text-gray-500 font-normal whitespace-nowrap mr-1">
                                        {rangeStart.toLocaleString("en-IN")}–{rangeEnd.toLocaleString("en-IN")} of {totalElements.toLocaleString("en-IN")}
                                    </span>
                                )}
                                <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                                    className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    ‹ Prev
                                </button>
                                {paginationPages.map((p, i) =>
                                    p === "..." ? (
                                        <span key={`e${i}`} className="h-8 w-8 flex items-center justify-center text-gray-400 text-sm select-none flex-shrink-0">…</span>
                                    ) : (
                                        <button key={p} onClick={() => handleFetchForPage(p - 1)}
                                            className={`h-8 min-w-[34px] px-2 rounded-lg text-[13px] font-medium transition border flex-shrink-0 ${page === p - 1
                                                ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                                : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                            }`}>
                                            {p}
                                        </button>
                                    )
                                )}
                                <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                                    className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    Next ›
                                </button>
                            </div>

                            {/* RIGHT: Rows + Go to page */}
                            <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
                                <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Rows</span>
                                {[50, 100, 500, 1000].map((s) => (
                                    <button key={s} onClick={() => { setSize(s); setPage(0); fetchData(0); }}
                                        className={`h-8 min-w-[44px] px-3 rounded-full text-[13px] font-medium transition border whitespace-nowrap flex-shrink-0 ${size === s
                                            ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                            : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F] hover:border-[#0A2A4F]/30"
                                        }`}>
                                        {s}
                                    </button>
                                ))}
                                <div className="w-px h-6 bg-[#E5E7EB] flex-shrink-0 mx-1" />
                                <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Go to</span>
                                <input type="number" min={1} max={totalPages} placeholder="Page" value={goToPage}
                                    onChange={(e) => { setGoToPage(e.target.value); setDownloadError(""); }}
                                    className="h-8 w-20 px-2.5 border border-[#E5E7EB] rounded-lg bg-white text-[13px] font-normal text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none flex-shrink-0" />
                                <button onClick={handleGoToPage}
                                    className="h-8 px-4 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[13px] transition flex-shrink-0">
                                    Go
                                </button>
                                {downloadError && (
                                    <span className="text-[12px] text-red-600 font-normal bg-red-50 border border-red-200 rounded-lg px-2 py-1 whitespace-nowrap">
                                        ⚠ {downloadError}
                                    </span>
                                )}
                            </div>

                        </div>
                    )}

                </div>
            </div>

            {/* ✅ TOAST NOTIFICATION */}
            {toast && (
                <div className={`fixed top-5 right-5 z-[80] flex items-start gap-3 px-5 py-4 rounded-2xl shadow-xl border max-w-sm animate-in fade-in slide-in-from-top-4 ${
                    toast.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"
                }`}>
                    <span className="text-lg leading-none mt-0.5 font-bold">
                        {toast.type === "success" ? "✓" : "✕"}
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">
                            {toast.type === "success" ? "Success" : "Error"}
                        </p>
                        <p className="text-xs mt-0.5 opacity-80 break-words">{toast.message}</p>
                    </div>
                    <button onClick={() => setToast(null)} className="text-current opacity-40 hover:opacity-70 text-sm leading-none flex-shrink-0">✕</button>
                </div>
            )}

            {/* ✅ RECONSTRUCTED OD REPORT MODAL */}
            {showReconstructModal && reconstructData && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                        
                        <div className="bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] px-6 py-4 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-white">Reconstructed OD Report</h2>
                                <p className="text-blue-200 text-xs mt-0.5">
                                    Date: {reconstructDate} • {reconstructData.rows.length} Records Found
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={downloadReconstructCSV}
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition shadow-sm"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                    Download CSV
                                </button>
                                <button 
                                    onClick={() => setShowReconstructModal(false)} 
                                    className="text-white/70 hover:text-white text-2xl leading-none transition"
                                    title="Close"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div className="overflow-auto p-4 flex-1 bg-gray-50">
                            <table className="w-full text-left border-collapse whitespace-nowrap text-sm bg-white border border-gray-200 shadow-sm rounded-lg">
                                <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        {reconstructData.headers.map((header, i) => (
                                            <th key={i} className="px-4 py-3 border-b border-gray-200 font-bold text-gray-700 tracking-wide text-[11px] uppercase">
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {reconstructData.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={reconstructData.headers.length} className="px-4 py-8 text-center text-gray-500 font-medium">
                                                No data available for this date.
                                            </td>
                                        </tr>
                                    ) : (
                                        reconstructData.rows.map((row, i) => (
                                            <tr key={i} className="hover:bg-blue-50/50 transition">
                                                {reconstructData.headers.map((header, j) => {
                                                    const val = row[header];
                                                    const isAmount = header.toLowerCase().includes('amount') || header.toLowerCase().includes('limit');
                                                    return (
                                                        <td key={j} className={`px-4 py-2.5 text-xs text-gray-700 ${isAmount || header === "S.No." ? 'font-semibold text-gray-900' : ''}`}>
                                                            {isAmount && val && !isNaN(val) ? `₹${Number(val).toLocaleString("en-IN")}` : (val || "—")}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OdOutstanding;
